import { useAuth } from "@/auth/AuthContext";
import { Navigate, useLocation } from "react-router-dom";
import LegalAcceptance from "@/pages/LegalAcceptance";
import EmailVerificationRequired from "@/pages/EmailVerificationRequired";
import { useI18n } from "@/i18n/I18nContext";

/**
 * Fase 6.5B.3 legal-status error card — inline component so we avoid
 * adding another file for a 25-line render. Shown by RequireAuth when
 * /legal/status failed and the user still has an authenticated cookie
 * (fail-closed).
 */
function LegalStatusErrorCard() {
  const { t } = useI18n();
  const { retryLegalStatus, logout } = useAuth();
  return (
    <div className="min-h-screen grid place-items-center px-4 py-10 bg-[hsl(var(--bg))]" data-testid="legal-status-error">
      <div className="card p-6 max-w-md w-full">
        <div className="eyebrow mb-2">Rostr</div>
        <h1 className="font-display text-[22px] tracking-tight text-[hsl(var(--text-strong))] mb-2">
          {t("legal_status_error_title")}
        </h1>
        <p className="text-[14px] text-[hsl(var(--text-muted))] mb-4">
          {t("legal_status_error_body")}
        </p>
        <div className="flex gap-2 justify-end">
          <button onClick={logout} className="btn btn-ghost" data-testid="legal-status-error-logout">
            {t("legal_logout")}
          </button>
          <button onClick={retryLegalStatus} className="btn btn-primary" data-testid="legal-status-error-retry">
            {t("legal_status_error_retry")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Fase 6.5B.3 + Phase 8.1 render precedence:
 *   1. user === null            → booting loading.
 *   2. user === false           → redirect to /login.
 *   3. legalStatusError         → safe error card (fail-closed).
 *   4. !legalStatusLoaded       → legal-loading spinner.
 *   5. email NOT verified       → EmailVerificationRequired gate.
 *   6. legalRequired            → LegalAcceptance.
 *   7. role mismatch            → role-based redirect.
 *   8. authorized               → render children.
 *
 * Precedence rationale: the email-verification gate sits **before**
 * legal because a user cannot be considered "the party accepting
 * Terms" until we have proof they own the email. The backend enforces
 * the same order via its middleware chain (see server.py).
 */
export default function RequireAuth({ children, roles }) {
  const { user, legalRequired, legalStatusLoaded, legalStatusError } = useAuth();
  const loc = useLocation();

  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-muted-foreground" data-testid="require-auth-loading">
        <div className="animate-pulse">Rooster ...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (legalStatusError) return <LegalStatusErrorCard />;
  if (!legalStatusLoaded) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-muted-foreground" data-testid="require-auth-legal-loading">
        <div className="animate-pulse">Rooster ...</div>
      </div>
    );
  }
  // Phase 8.1 — email verification gate. Users whose account was
  // created via Google Sign-In or invitation redemption are already
  // marked verified on the backend and won't hit this branch.
  if (user.email_verified === false) return <EmailVerificationRequired />;
  if (legalRequired) return <LegalAcceptance />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "employee" ? "/my-roster" : "/dashboard"} replace />;
  }
  return children;
}
